// src/utils/geolocation.js
export class GeoLocationUtil {
  constructor(config = {}) {
    this.config = config;
    this.ipLookupUrl = config.ipLookupUrl || 'https://ipapi.co/json/'; // fallback IP geolocation
    this.timeout = config.timeout || 8000;
  }

  // Try browser geolocation first, then IP-based fallback.
  async getUserLocation() {
    // Guard for non-browser environments
    if (typeof window === 'undefined') {
      return this._fallbackLocation('Unknown', null);
    }

    // Try navigator.geolocation
    if (navigator?.geolocation) {
      try {
        const pos = await this._getCurrentPosition(this.timeout);
        return {
          source: 'browser',
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: new Date(pos.timestamp).toISOString()
        };
      } catch (err) {
        // fall through to IP fallback
        this._debug('Browser geolocation failed:', err && err.message);
      }
    }

    // IP based lookup
    try {
      const res = await fetch(this.ipLookupUrl, { cache: 'no-store' });
      if (!res.ok) throw new Error('IP lookup failed');
      const json = await res.json();
      return {
        source: 'ip',
        ip: json.ip || json?.query || null,
        country: json.country_name || json.country || json?.regionName || null,
        region: json.region || json.region_name || null,
        city: json.city || null,
        lat: json.latitude || json.lat || null,
        lon: json.longitude || json.lon || null,
        org: json.org || json.isp || null,
        raw: json,
        timestamp: new Date().toISOString()
      };
    } catch (err) {
      this._debug('IP lookup failed:', err && err.message);
      return this._fallbackLocation('Unknown', null);
    }
  }

  // small helper to obtain the browser position as a promise
  _getCurrentPosition(timeout = 8000) {
    return new Promise((resolve, reject) => {
      let timer = null;
      navigator.geolocation.getCurrentPosition(
        pos => {
          if (timer) clearTimeout(timer);
          resolve(pos);
        },
        err => {
          if (timer) clearTimeout(timer);
          reject(err);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout }
      );

      timer = setTimeout(() => {
        reject(new Error('Geolocation timeout'));
      }, timeout + 100);
    });
  }

  hasLocationChanged(prev, current, thresholdMeters = 500) {
    if (!prev || !current) return true;
    // if either uses IP and doesn't have coords, compare country
    if (!prev.lat || !prev.lon || !current.lat || !current.lon) {
      return prev.country !== current.country || prev.ip !== current.ip;
    }
    const d = this._haversineDistance(prev.lat, prev.lon, current.lat, current.lon);
    return d > thresholdMeters;
  }

  // meters
  _haversineDistance(lat1, lon1, lat2, lon2) {
    const toRad = v => (v * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  _fallbackLocation(country = 'Unknown', ip = null) {
    return {
      source: 'fallback',
      country,
      ip,
      timestamp: new Date().toISOString()
    };
  }

  _debug(...args) {
    if (this.config.debug) console.debug('[GeoLocationUtil]', ...args);
  }
}
